"use client";

import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

interface TooltipProps {
  /** Plain-text body shown when hovered/focused. */
  text: string;
  /** Optional title above the text — useful for term-glossary callouts. */
  title?: string;
  /** The trigger UI (text, icon, button, etc). Wrapped in an inline span. */
  children: ReactNode;
  /** Visual style of the trigger. `plain` avoids link-like decoration in long prose. */
  triggerStyle?: "dotted" | "plain";
  className?: string;
  style?: CSSProperties;
}

/**
 * Hover/focus tooltip for web — pure CSS via `:hover` and `:focus-within`,
 * no JS state. Designed for inline glossary terms (dosha, nakshatra, lagna,
 * dasha, etc) on the Jyotish calculator pages.
 *
 * Accessibility:
 *   - Trigger is `tabIndex=0` so keyboard users can focus and read.
 *   - The popover is announced via `aria-describedby` linking
 *     trigger -> popover by id.
 *   - On touch, tapping the trigger fires `:focus-within`, so it works
 *     without hover.
 */
export default function Tooltip({
  text,
  title,
  children,
  triggerStyle = "plain",
  className,
  style,
}: TooltipProps) {
  const popoverId = useId();
  const popoverRef = useRef<HTMLSpanElement>(null);
  // Track hover/focus locally so we can swap the popover's `display`. CSS-only
  // would work but inline styles can't target descendants on hover, and we'd
  // rather not ship a stylesheet for one primitive.
  const [open, setOpen] = useState(false);
  const [popoverOffsetX, setPopoverOffsetX] = useState(0);

  useLayoutEffect(() => {
    if (!open || !popoverRef.current) return;

    const margin = 8;
    const rect = popoverRef.current.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    let nextOffset = 0;

    if (rect.left < margin) {
      nextOffset = margin - rect.left;
    } else if (rect.right > viewportWidth - margin) {
      nextOffset = viewportWidth - margin - rect.right;
    }

    setPopoverOffsetX(nextOffset);
  }, [open, text, title]);

  const triggerCss: CSSProperties = {
    position: "relative",
    display: "inline-block",
    cursor: "help",
    borderBottom: triggerStyle === "dotted" ? "1px dotted currentColor" : "none",
    ...style,
  };

  const popoverCss: CSSProperties = {
    position: "absolute",
    bottom: "calc(100% + 6px)",
    left: "50%",
    transform: `translateX(calc(-50% + ${popoverOffsetX}px))`,
    minWidth: 200,
    maxWidth: "min(280px, calc(100vw - 16px))",
    background: "var(--ish-bg-elevated, #1f1f23)",
    color: "var(--ish-text-on-dark, #fff)",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 13,
    lineHeight: 1.45,
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
    zIndex: 50,
    pointerEvents: "none",
    display: open ? "block" : "none",
    opacity: open ? 1 : 0,
    visibility: open ? "visible" : "hidden",
    transition: "opacity 120ms ease",
  };

  return (
    <span
      className={className}
      style={triggerCss}
      tabIndex={0}
      aria-describedby={open ? popoverId : undefined}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        setOpen(false);
        setPopoverOffsetX(0);
      }}
      onFocus={() => setOpen(true)}
      onBlur={() => {
        setOpen(false);
        setPopoverOffsetX(0);
      }}
    >
      {children}
      <span ref={popoverRef} id={popoverId} role="tooltip" style={popoverCss}>
        {title ? (
          <strong style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>{title}</strong>
        ) : null}
        {text}
      </span>
    </span>
  );
}
